import type { Stripe } from "@stripe/stripe-js";

export type StripeJsLoadStatus = "loading" | "ready" | "error";

export type StripeJsLoadEvent = "ready" | "load_error" | "retry";

export const STRIPE_JS_LOAD_BLOCKED_CODE = "stripe_js_load_failed" as const;

export const STRIPE_JS_LOAD_MAX_ATTEMPTS = 2;

export const STRIPE_JS_LOAD_RETRY_DELAY_MS = 500;

type LoadStripeFn = (publishableKey: string) => Promise<Stripe | null>;

/**
 * Reduce Stripe.js script-load UI state.
 *
 * Late load_error after ready must not flip back to error.
 *
 * @param status - Current load status
 * @param event - Lifecycle event from the loader or retry
 * @returns Next load status
 */
export const reduceStripeJsLoadState = (
	status: StripeJsLoadStatus,
	event: StripeJsLoadEvent,
): StripeJsLoadStatus => {
	switch (event) {
		case "ready":
			return "ready";
		case "load_error":
			return status === "ready" ? "ready" : "error";
		case "retry":
			return "loading";
	}
};

/**
 * PostHog `checkout_blocked` error_code for Stripe.js script load failures.
 *
 * @returns Dedicated blocked code for analytics
 */
export const getStripeJsLoadBlockedCode =
	(): typeof STRIPE_JS_LOAD_BLOCKED_CODE => {
		return STRIPE_JS_LOAD_BLOCKED_CODE;
	};

/**
 * Short user-facing copy when Stripe.js never loads.
 *
 * @param options.canUsePayPal - PayPal is already offered on this surface
 * @returns Error message for the load-failure UI
 */
export const getStripeJsLoadErrorMessage = (options: {
	canUsePayPal?: boolean;
}): string => {
	if (options.canUsePayPal) {
		return "Payment form failed to load. Try again, or use PayPal instead.";
	}

	return "Payment form failed to load. Please try again.";
};

const delay = (ms: number): Promise<void> =>
	new Promise((resolve) => {
		setTimeout(resolve, ms);
	});

/**
 * Load Stripe.js with a small number of retries so a single CDN blip does not
 * leave checkout stuck on "Loading card form…".
 *
 * @param publishableKey - Stripe publishable key
 * @param options.loadStripe - Injectable loader (defaults to @stripe/stripe-js/pure)
 * @param options.maxAttempts - Total attempts including the first
 * @param options.retryDelayMs - Delay between attempts
 * @returns Resolved Stripe instance
 */
export const loadStripeWithRetry = async (
	publishableKey: string,
	options: {
		loadStripe: LoadStripeFn;
		maxAttempts?: number;
		retryDelayMs?: number;
	},
): Promise<Stripe> => {
	const maxAttempts = options.maxAttempts ?? STRIPE_JS_LOAD_MAX_ATTEMPTS;
	const retryDelayMs = options.retryDelayMs ?? STRIPE_JS_LOAD_RETRY_DELAY_MS;

	let lastError: unknown = new Error("Failed to load Stripe.js");

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		try {
			const stripe = await options.loadStripe(publishableKey);
			if (stripe) {
				return stripe;
			}
			lastError = new Error("Failed to load Stripe.js");
		} catch (error) {
			lastError = error;
		}

		if (attempt < maxAttempts - 1) {
			await delay(retryDelayMs);
		}
	}

	throw lastError instanceof Error
		? lastError
		: new Error("Failed to load Stripe.js");
};
